import { lowerFirst, uniqueId } from 'lodash';
import {
  ActionType,
  SendMessageActionFormData,
  SendMessageRecipient,
  SendMessageType,
  SIMPLE_NOTIFICATIONS_ALERT_EVENT_BC,
  SimplifiedWorkflowActionStepFormData,
  SimplifiedWorkflowStepFormData,
  SimplifiedWorkflowTriggerStepFormData,
  SimplifiedWorkflowWait,
  StepType,
} from '../data/models/simplified-workflow.model';
import {
  WorkflowActions,
  WorkflowActivateWillCallAction,
  WorkflowBaseDateTimeRef,
  WorkflowCancelAction,
  WorkflowCancelTripLegAction,
  WorkflowConditionAction,
  WorkflowConditionalBC,
  WorkflowConditionOutcomeStatus,
  WorkflowPublishAlertAction,
  WorkflowStep,
  WorkflowWaitAction,
} from '../data/models/workflow.model';
import {
  WaitActionTimePeriod,
  WaitAmountUnit,
} from '../data/workflow-form.model';
import describeWorkflowUtils from './describeWorkflowUtils';
import workflowUtils from './workflowUtils';
import { TFunction } from 'i18next';
import simplifiedWorkflowUtils from './simplifiedWorkflowUtils';
import { SmartLabel } from '../data/models/smart-label.model';
import { branchingEdgeName } from '../components/home/workflows/WorkflowFlowChart';

interface SendMessageMetadata {
  recipient: string;
  trigger: string;
  type: string;
}

export class TripLegPickupMessageModelTransformer
  implements SimplifiedWorkflowModelTransformer
{
  canTransform(steps: WorkflowStep[]): boolean {
    if (steps.length !== 4) {
      return false;
    }
    const startStep = steps[0];
    const secondStep = steps[1];
    const thirdStep = steps[2];
    const fourthStep = steps[3];
    const firstStepAction = startStep.action as WorkflowWaitAction;
    const secondStepAction = secondStep.action as WorkflowWaitAction;
    if (
      firstStepAction.wait &&
      firstStepAction.wait.baseDateTimeRef ===
        WorkflowBaseDateTimeRef.TripLegPickup &&
      secondStepAction.wait &&
      secondStepAction.wait.baseDateTimeRef ===
        WorkflowBaseDateTimeRef.TripLegPickup &&
      !startStep.onCancel &&
      !startStep.onFailure &&
      startStep?.onSuccess?.triggerStep === thirdStep.name &&
      isSendMessageStep(thirdStep) &&
      (fourthStep.action as WorkflowCancelAction).cancel
    ) {
      return true;
    }
    return false;
  }

  getSendMessageMetadata(steps: WorkflowStep[]): SendMessageMetadata {
    if (steps.length >= 3) {
      const metadata = getSendMessageMetadata(steps[0], steps[2]);
      metadata.trigger = metadata.trigger.replace(
        'TripLegOpened',
        WorkflowBaseDateTimeRef.TripLegPickup,
      );
      return metadata;
    }
    return {
      recipient: '',
      trigger: '',
      type: '',
    };
  }

  transform(
    steps: WorkflowStep[],
    translateFunction: TFunction,
    smartLabels: SmartLabel[],
  ): SimplifiedWorkflowStepFormData[] {
    if (steps.length === 4) {
      const triggerStep = getTriggerStep(steps[0], steps[2], translateFunction);
      triggerStep.event = WorkflowBaseDateTimeRef.TripLegPickup;
      triggerStep.title = triggerStep.title.replace(
        'TripLegOpened',
        WorkflowBaseDateTimeRef.TripLegPickup,
      );
      return [
        triggerStep,
        getActionStep(
          steps[2],
          translateFunction,
          smartLabels,
          triggerStep.tempId,
        ),
      ];
    }
    return [];
  }
}

export class SendMessageModelTransformer
  implements SimplifiedWorkflowModelTransformer
{
  canTransform(steps: WorkflowStep[]): boolean {
    if (steps.length === 1) {
      const startStep = steps[0];
      return isSendMessageStep(startStep);
    } else if (steps.length === 2) {
      const startStep = steps[0];
      const action = startStep.action as WorkflowWaitAction;
      if (
        action.wait &&
        !startStep.onCancel &&
        !startStep.onFailure &&
        startStep?.onSuccess?.triggerStep === steps[1].name
      ) {
        return isSendMessageStep(steps[1]);
      }
    }
    return false;
  }
  getSendMessageMetadata(steps: WorkflowStep[]): SendMessageMetadata {
    if (steps.length === 1) {
      return getSendMessageMetadata(steps[0], steps[0]);
    } else if (steps.length === 2) {
      return getSendMessageMetadata(steps[0], steps[1]);
    }
    return {
      recipient: '',
      trigger: '',
      type: '',
    };
  }

  transform(
    steps: WorkflowStep[],
    translateFunction: TFunction,
    smartLabels: SmartLabel[],
  ): SimplifiedWorkflowStepFormData[] {
    if (steps.length === 1) {
      const triggerStep = getTriggerStep(steps[0], steps[0], translateFunction);
      return [
        triggerStep,
        getActionStep(
          steps[0],
          translateFunction,
          smartLabels,
          triggerStep.tempId,
        ),
      ];
    } else if (steps.length === 2) {
      const triggerStep = getTriggerStep(steps[0], steps[1], translateFunction);
      return [
        triggerStep,
        getActionStep(
          steps[1],
          translateFunction,
          smartLabels,
          triggerStep.tempId,
        ),
      ];
    }
    return [];
  }
}

export class SendInteractiveResponseMessageTransformer
  implements SimplifiedWorkflowModelTransformer
{
  getSendMessageMetadata(steps: WorkflowStep[]): SendMessageMetadata {
    if ((steps[0].action as WorkflowWaitAction).wait) {
      return getSendMessageMetadata(steps[0], steps[1]);
    } else {
      return getSendMessageMetadata(steps[0], steps[0]);
    }
  }

  canTransform(steps: WorkflowStep[]): boolean {
    if (steps.length >= 2) {
      return (
        isInteractiveSendMessageStep(steps[0]) ||
        isInteractiveSendMessageStep(steps[1])
      );
    }
    return false;
  }

  transform(
    steps: WorkflowStep[],
    translateFunction: TFunction,
    smartLabels: SmartLabel[],
  ): SimplifiedWorkflowStepFormData[] {
    const stepsToReturn: SimplifiedWorkflowStepFormData[] = [];
    let nextStepIndex = 0;
    let sendMessageStepId: string | undefined;
    if ((steps[0].action as WorkflowWaitAction).wait) {
      const triggerStep = getTriggerStep(steps[0], steps[1], translateFunction);
      stepsToReturn.push(triggerStep);
      const actionStep = getActionStep(
        steps[1],
        translateFunction,
        smartLabels,
        triggerStep.tempId,
      );
      sendMessageStepId = actionStep.tempId;
      stepsToReturn.push(actionStep);
      nextStepIndex = 2;
    } else {
      const triggerStep = getTriggerStep(steps[0], steps[0], translateFunction);
      stepsToReturn.push(triggerStep);
      const actionStep = getActionStep(
        steps[0],
        translateFunction,
        smartLabels,
        triggerStep.tempId,
      );
      sendMessageStepId = actionStep.tempId;
      stepsToReturn.push(actionStep);
      nextStepIndex = 1;
    }
    const branchingTriggers = getBranchingTriggers(steps[nextStepIndex]);
    const sendMessageStep = steps[nextStepIndex - 1];
    if (sendMessageStep.onFailure?.triggerStep) {
      branchingTriggers[
        workflowUtils.getConditionalName(WorkflowConditionalBC.Timeout)!
      ] = sendMessageStep.onFailure.triggerStep;
    }
    (
      stepsToReturn[stepsToReturn.length - 1] as SendMessageActionFormData
    ).branches = Object.keys(branchingTriggers);
    const children = Object.entries(branchingTriggers)
      .map(([branchName, triggerStep]) => {
        const step = steps.find((s) => s.name === triggerStep);
        if (step && sendMessageStepId) {
          const childStep = getStep(
            step,
            sendMessageStepId,
            branchName,
            smartLabels,
            translateFunction,
          );
          if (childStep) {
            return [
              childStep,
              ...getDescendantSteps(
                step,
                steps,
                childStep.tempId!,
                branchName,
                smartLabels,
                translateFunction,
              ),
            ];
          }
        }
      })
      .flat()
      .filter((s) => !!s) as SimplifiedWorkflowActionStepFormData[];
    stepsToReturn.push(...children);
    return stepsToReturn;
  }
}

export class SimplifiedWorkflowModelTransformerRegistry {
  private transformers: SimplifiedWorkflowModelTransformer[] = [
    new TripLegPickupMessageModelTransformer(),
    new SendInteractiveResponseMessageTransformer(),
    new SendMessageModelTransformer(),
  ];

  getTransformer(
    steps: WorkflowStep[],
  ): SimplifiedWorkflowModelTransformer | undefined {
    const transformer = this.transformers.find((t) => t.canTransform(steps));
    return transformer;
  }
}

export interface SimplifiedWorkflowModelTransformer {
  canTransform(steps: WorkflowStep[]): boolean;
  getSendMessageMetadata(steps: WorkflowStep[]): SendMessageMetadata;
  transform(
    steps: WorkflowStep[],
    translateFunction: TFunction,
    smartLabels: SmartLabel[],
  ): SimplifiedWorkflowStepFormData[];
}

function isSendMessageStep(step: WorkflowStep) {
  const action = step.action as WorkflowPublishAlertAction;
  return (
    action.publishAlert &&
    action.publishAlert.alertEventBC === SIMPLE_NOTIFICATIONS_ALERT_EVENT_BC &&
    !step.onCancel &&
    !step.onFailure &&
    !step.onSuccess
  );
}

function isInteractiveSendMessageStep(step: WorkflowStep) {
  const action = step.action as WorkflowPublishAlertAction;
  return (
    action.publishAlert &&
    action.publishAlert.alertEventBC === SIMPLE_NOTIFICATIONS_ALERT_EVENT_BC &&
    action.publishAlert.interactiveResponse &&
    !!step.onSuccess
  );
}

function getSendMessageMetadata(
  triggerStep: WorkflowStep,
  sendMessageStep: WorkflowStep,
): SendMessageMetadata {
  const triggerEvent = triggerStep.trigger?.events?.[0] ?? {
    name: '',
    version: 1,
  };
  const waitAction = (triggerStep.action as WorkflowWaitAction)?.wait;
  const waitActionParams = waitAction
    ? workflowUtils.getWaitActionParams(
        triggerStep.action as WorkflowWaitAction,
      )
    : undefined;
  let triggerDesc = '';
  if (waitActionParams) {
    const waitAmountUnit = describeWorkflowUtils.getFormattedWaitAmountUnit(
      waitActionParams.waitAmount ?? '0',
      waitActionParams.waitAmountUnit ?? '',
    );
    triggerDesc = `${waitActionParams.waitAmount} ${waitAmountUnit} ${waitActionParams.waitTimePeriod} ${triggerEvent.name}`;
  } else {
    triggerDesc = triggerEvent.name;
  }
  const pubAlertAction = sendMessageStep.action as WorkflowPublishAlertAction;
  const recipient = pubAlertAction?.publishAlert.alertRecipientBC ?? '';
  const type = pubAlertAction?.publishAlert.alertTypeBC ?? '';
  return {
    recipient,
    type,
    trigger: triggerDesc,
  };
}

function getTriggerStep(
  startStep: WorkflowStep,
  sendMessageStep: WorkflowStep,
  translateFunction: TFunction,
): SimplifiedWorkflowTriggerStepFormData {
  let wait: SimplifiedWorkflowWait;
  if (startStep === sendMessageStep) {
    wait = {
      waitAmount: '0',
      waitAmountUnit: WaitAmountUnit.Days,
      waitTimePeriod: WaitActionTimePeriod.After,
    };
  } else {
    const waitAction = startStep.action as WorkflowWaitAction;
    const waitParams = workflowUtils.getWaitActionParams(waitAction);
    wait = {
      waitAmount: waitParams.waitAmount,
      waitAmountUnit: waitParams.waitAmountUnit,
      waitTimePeriod: waitParams.waitTimePeriod,
    };
  }
  const event = startStep.trigger?.events?.[0] ?? '';
  const eventName = event ? event.name : '';
  return {
    title: translateFunction('workflows.triggerWithEvent', {
      event: eventName,
    }),
    type: StepType.Trigger,
    tempId: uniqueId(),
    event: eventName,
    wait,
  };
}

function getActionStep(
  sendMessageStep: WorkflowStep,
  translateFunction: TFunction,
  smartLabels: SmartLabel[],
  parentId?: string,
): SendMessageActionFormData {
  const publishAction = sendMessageStep.action as WorkflowPublishAlertAction;
  const publishAlert = publishAction.publishAlert;
  const actionTypeTranslated = translateFunction(
    `workflows.${lowerFirst(ActionType.SendMessage)}Action`,
  );
  return {
    title: translateFunction('workflows.actionWithType', {
      actionType: actionTypeTranslated,
    }),
    type: StepType.Action,
    tempId: uniqueId(),
    actionType: ActionType.SendMessage,
    messageType: publishAlert.alertTypeBC as SendMessageType,
    recipient: publishAlert.alertRecipientBC as SendMessageRecipient,
    message: simplifiedWorkflowUtils.replaceSmartLabelBCWithName(
      publishAlert.alertDescription ?? '',
      smartLabels,
    ),
    parentId,
    interactiveResponse: publishAlert.interactiveResponse ?? false,
    responseTimeoutMinutes: toString(publishAlert.responseTimeoutMinutes),
    sendReminderIfNoResponse: publishAlert.sendReminderIfNoResponse ?? false,
    numberOfReminders: toString(publishAlert.numberOfReminders),
    sendReminderAfterMinutes: toString(publishAlert.sendReminderAfterMinutes),
    alertReminder: publishAlert.alertReminder,
    sameAsInitialMessage:
      publishAlert.alertDescription === publishAlert.alertReminder,
    supportsInteractive: true,
    alertForSTOReservations: publishAlert.alertForSTOReservations ?? false,
  };
}

function getBranchingTriggers(branchingStep: WorkflowStep): {
  [key: string]: string;
} {
  const branchingAction = branchingStep.action as WorkflowConditionAction;
  const triggerStepMap: { [key: string]: string } = {};
  const successStepName = branchingStep.onSuccess?.triggerStep ?? '';
  const failStepName = branchingStep.onFailure?.triggerStep ?? '';
  branchingAction.condition.cases.forEach((c) => {
    const branchName = workflowUtils.getConditionalName(c.when.value);
    if (branchName) {
      triggerStepMap[branchName] =
        c.outcomeStatus === WorkflowConditionOutcomeStatus.Succeeded
          ? successStepName
          : failStepName;
    }
  });
  if (branchingAction.condition.elseCase) {
    const branchName = workflowUtils.getConditionalName(
      WorkflowConditionalBC.Negate,
    )!;
    triggerStepMap[branchName] = failStepName;
  }
  return triggerStepMap;
}

function getDescendantSteps(
  step: WorkflowStep,
  allSteps: WorkflowStep[],
  parentId: string,
  branch: string,
  smartLabels: SmartLabel[],
  translateFunction: TFunction,
): SimplifiedWorkflowActionStepFormData[] {
  if (step.onSuccess) {
    const nextStepName = step.onSuccess.triggerStep;
    const nextStep = allSteps.find((s) => s.name === nextStepName);
    if (nextStep) {
      const nextStepForm = getStep(
        nextStep,
        parentId,
        branch,
        smartLabels,
        translateFunction,
      );
      if (nextStepForm) {
        return [
          nextStepForm,
          ...getDescendantSteps(
            nextStep,
            allSteps,
            nextStepForm.tempId!,
            branch,
            smartLabels,
            translateFunction,
          ),
        ];
      }
    }
  }
  return [];
}

function getStep(
  step: WorkflowStep,
  parentId: string,
  branch: string,
  smartLabels: SmartLabel[],
  translateFunction: TFunction,
): SimplifiedWorkflowActionStepFormData | undefined {
  const action = step.action;
  if ((action as WorkflowActivateWillCallAction).activateWillCall) {
    return getActivateWillCallStep(step, parentId, branch, translateFunction);
  } else if ((action as WorkflowCancelTripLegAction).cancelTripLeg) {
    return getCancelTripLegStep(step, parentId, branch, translateFunction);
  } else if ((action as WorkflowPublishAlertAction).publishAlert) {
    return getSendMessageStep(
      step,
      parentId,
      branch,
      smartLabels,
      translateFunction,
    );
  }
  return undefined;
}

function getActivateWillCallStep(
  step: WorkflowStep,
  parentId: string,
  branch: string,
  translateFunction: TFunction,
): SimplifiedWorkflowActionStepFormData {
  const actionTypeTranslated = translateFunction(
    `workflows.${lowerFirst(ActionType.ActivateWillCall)}Action`,
  );
  return {
    actionType: ActionType.ActivateWillCall,
    title: translateFunction('workflows.actionWithType', {
      actionType: actionTypeTranslated,
    }),
    type: StepType.Action,
    tempId: uniqueId(),
    parentId,
    branch,
  };
}

function getCancelTripLegStep(
  step: WorkflowStep,
  parentId: string,
  branch: string,
  translateFunction: TFunction,
): SimplifiedWorkflowActionStepFormData {
  const actionTypeTranslated = translateFunction(
    `workflows.${lowerFirst(ActionType.CancelReturnTrip)}Action`,
  );
  return {
    actionType: ActionType.CancelReturnTrip,
    title: translateFunction('workflows.actionWithType', {
      actionType: actionTypeTranslated,
    }),
    type: StepType.Action,
    tempId: uniqueId(),
    parentId,
    branch,
  };
}

function getSendMessageStep(
  step: WorkflowStep,
  parentId: string,
  branch: string,
  smartLabels: SmartLabel[],
  translateFunction: TFunction,
): SendMessageActionFormData {
  const publishAction = step.action as WorkflowPublishAlertAction;
  const publishAlert = publishAction.publishAlert;
  const actionTypeTranslated = translateFunction(
    `workflows.${lowerFirst(ActionType.SendMessage)}Action`,
  );
  return {
    actionType: ActionType.SendMessage,
    title: translateFunction('workflows.actionWithType', {
      actionType: actionTypeTranslated,
    }),
    type: StepType.Action,
    tempId: uniqueId(),
    parentId,
    branch,
    interactiveResponse: false,
    supportsInteractive: false,
    messageType: publishAlert.alertTypeBC as SendMessageType,
    recipient: publishAlert.alertRecipientBC as SendMessageRecipient,
    message: simplifiedWorkflowUtils.replaceSmartLabelBCWithName(
      publishAlert.alertDescription ?? '',
      smartLabels,
    ),
    alertForSTOReservations: publishAlert.alertForSTOReservations ?? false,
  };
}

function toString(value?: number) {
  if (value) {
    return `${value}`;
  }
  return undefined;
}
